import React, { useState, useEffect } from 'react';
import { Camera, Share2, ExternalLink, Image, Heart, BarChart3, Calendar, Hash, Upload } from 'lucide-react';
import FeatureCard from '../components/FeatureCard';
import { fetchGalleryFilters, fetchHistoricalPhotos } from '../data/mockData';

// Home Page Component
const HomePage = ({ navigateTo }) => {
  const [stats, setStats] = useState({
    totalPhotos: 0,
    categories: 0,
    yearSpan: '1994-2025',
    loading: true
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        // Carica i filtri per ottenere il numero totale di foto e categorie
        const filters = await fetchGalleryFilters();
        
        // Carica alcune foto per calcolare il range di anni
        const photosData = await fetchHistoricalPhotos({ all: true });
        
        const totalPhotos = filters.find(f => f.id === 'all')?.count || 0;
        const categories = filters.filter(f => f.id !== 'all').length;
        
        // Calcola il range di anni dalle foto
        let minYear = 1994;
        let maxYear = new Date().getFullYear();
        
        if (photosData.photos && photosData.photos.length > 0) {
          const years = photosData.photos
            .map(photo => {
              // Estrai l'anno dal category/year field, oppure dal created_at
              if (photo.year && !isNaN(photo.year)) {
                return parseInt(photo.year);
              }
              // Fallback: estrai dall'anno di created_at
              if (photo.created_at) {
                return new Date(photo.created_at).getFullYear();
              }
              return null;
            })
            .filter(year => year && year >= 1990 && year <= maxYear);
            
          if (years.length > 0) {
            minYear = Math.min(...years);
            maxYear = Math.max(...years);
          }
        }
        
        setStats({
          totalPhotos,
          categories,
          yearSpan: `1995-2025`,
          loading: false
        });
      } catch (error) {
        console.error('Error loading stats:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    loadStats();
  }, []);

  return (
    <div className="container mx-auto px-4 py-6 text-amber-100">
      <div className="mb-8 p-6 bg-stone-800 rounded-lg shadow-md">
        <h2 className="text-xl font-bold mb-4 text-center text-amber-300">30 Anni di Storia</h2>
        <p className="mb-4">
          Benvenuti nel museo digitale del Celtic Druid Irish Pub di Bologna, un viaggio interattivo attraverso 30 anni di storia, ricordi ed emozioni.
        </p>
        <p className="mb-4">
          Esplora le nostre fotografie storiche, ricrea i momenti più significativi e condividi i tuoi ricordi.
        </p>
        
        <div className="mt-6">
          <button 
            onClick={() => navigateTo('gallery')}
            className="w-full py-3 bg-green-800 hover:bg-green-700 text-amber-100 rounded-lg shadow transition-colors flex items-center justify-center"
          >
            <Image size={20} className="mr-2" />
            Esplora la Galleria Storica
          </button>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="mb-8 p-6 bg-gradient-to-r from-green-900 to-green-800 rounded-lg shadow-md">
        <h3 className="text-lg font-bold mb-4 text-center text-amber-300">La Nostra Storia in Numeri</h3>
        
        {stats.loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-300"></div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-stone-800 rounded-lg p-4">
              <div className="flex flex-col items-center">
                <BarChart3 size={20} className="text-amber-300 mb-2" />
                <div className="text-2xl font-bold text-amber-100">{stats.totalPhotos}</div>
                <div className="text-sm text-amber-200">Foto Storiche</div>
              </div>
            </div>
            
            <div className="bg-stone-800 rounded-lg p-4">
              <div className="flex flex-col items-center">
                <Calendar size={20} className="text-amber-300 mb-2" />
                <div className="text-2xl font-bold text-amber-100">{stats.yearSpan}</div>
                <div className="text-sm text-amber-200">Anni Coperti</div>
              </div>
            </div>
            
            <div className="bg-stone-800 rounded-lg p-4">
              <div className="flex flex-col items-center">
                <Hash size={20} className="text-amber-300 mb-2" />
                <div className="text-2xl font-bold text-amber-100">{stats.categories}</div>
                <div className="text-sm text-amber-200">Categorie</div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 gap-4 mb-8">
        <FeatureCard 
          icon={<Camera size={24} />}
          title="Crea Ricordi"
          description="Ricrea le foto storiche e confronta passato e presente"
          onClick={() => navigateTo('gallery')}
        />
        <FeatureCard 
          icon={<Upload size={24} />}
          title="Condividi le Tue Foto"
          description="Carica le tue foto del Celtic Druid e aiutaci a completare la storia"
          onClick={() => navigateTo('upload')}
          highlightColor="bg-amber-600 hover:bg-amber-500"
        />
        <FeatureCard 
          icon={<Share2 size={24} />}
          title="Condividi"
          description="Salva e condividi i tuoi ricordi sui social"
          onClick={() => navigateTo('gallery')}
        />
        <FeatureCard 
          icon={<ExternalLink size={24} />}
          title="Piattaforma StoryArch.io"
          description="Scopri di più sulla piattaforma utilizzata per creare questo museo digitale"
          onClick={() => window.open('https://storyarch.io', '_blank')}
        />
      </div>

      {/* Footer MPSH */}
      <div className="text-center py-4 mt-8 border-t border-stone-700">
        <p className="text-sm text-amber-200 flex items-center justify-center">
          Built with <Heart size={16} className="mx-1 text-red-500" /> by{' '}
          <a 
            href="https://mpsh.it" 
            target="_blank" 
            rel="noopener noreferrer"
            className="ml-1 text-amber-300 hover:text-amber-100 font-semibold underline transition-colors"
          >
            MPSH
          </a>
        </p>
      </div>
    </div>
  );
};

export default HomePage;